import { useRef, useState, useCallback } from "react";
import { Upload, FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";

interface FileUploadDropzoneProps {
  accept: string;
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onClear?: () => void;
  title?: string;
  description?: string;
  acceptedFormats?: string[];
  supportedFields?: Array<{ label: string; required?: boolean }>;
  disabled?: boolean;
  className?: string;
}

export function FileUploadDropzone({
  accept,
  onFileSelect,
  selectedFile,
  onClear,
  title = "Datei hochladen",
  description = "Ziehen Sie eine Datei hierher oder klicken Sie zum Auswählen",
  acceptedFormats,
  supportedFields,
  disabled = false,
  className,
}: FileUploadDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [disabled, onFileSelect]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClear?.();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Dropzone */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
          isDragOver && !disabled
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50",
          disabled && "opacity-50 cursor-not-allowed",
          selectedFile && "border-primary/50 bg-primary/5"
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />

        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <FileText className="h-10 w-10 text-primary" />
            <div className="text-left">
              <p className="font-medium">{selectedFile.name}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            {onClear && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className="ml-2"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">{title}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            </div>
            {acceptedFormats && acceptedFormats.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Unterstützte Formate: {acceptedFormats.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Supported Fields Info */}
      {supportedFields && supportedFields.length > 0 && !selectedFile && (
        <div className="text-sm">
          <p className="font-medium mb-2 text-muted-foreground">
            Importierbare Felder:
          </p>
          <div className="flex flex-wrap gap-2">
            {supportedFields.map((field, index) => (
              <Badge
                key={index}
                variant={field.required ? "default" : "secondary"}
                className={cn(
                  "text-xs",
                  field.required 
                    ? "bg-primary/10 text-primary hover:bg-primary/20" 
                    : "bg-muted hover:bg-muted/80"
                )}
              >
                {field.label}
                {field.required && " *"}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            * Pflichtfelder
          </p>
        </div>
      )}
    </div>
  );
}
